// /empresas/pedido/:token — la empresa envía sus tarjetas y ve cuáles se han canjeado.
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { getPedidoEmpresa, enviarTarjetasEmpresa, API_BASE } from '../lib/api';
import { tt } from '../lib/empresasTxt';

export default function EmpresaPedido() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const T = (k, v) => tt(lang, k, v);
  const [data, setData] = useState(null);
  const [fallo, setFallo] = useState(false);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState(null);
  const [cifF, setCifF] = useState('');
  const [errF, setErrF] = useState('');
  const [bajando, setBajando] = useState(false);

  const cargar = useCallback(() => getPedidoEmpresa(token).then(setData).catch(() => setFallo(true)), [token]);
  useEffect(() => { cargar(); }, [cargar]);
  // Si venimos de Stripe y el webhook aún no ha llegado, reintenta unos segundos.
  useEffect(() => {
    if (!data || data.pedido.estado === 'pagado' || params.get('stripe') !== 'success') return undefined;
    const id = setInterval(cargar, 3000);
    return () => clearInterval(id);
  }, [data, params, cargar]);

  if (fallo) return <main className="clase-page"><div className="clase-page-inner"><p>{T('no_encontrado')}</p></div></main>;
  if (!data) return <main className="clase-page"><div className="clase-page-inner"><p>{T('cargando')}</p></div></main>;
  const { pedido: p, tarjetas } = data;
  const pagado = p.estado === 'pagado';
  const porEnviar = tarjetas.filter((x) => x.estado === 'sin_enviar').length;

  const parsear = () => texto.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
    const partes = l.split(/[,;\t]/).map((s) => s.trim()).filter(Boolean);
    const email = partes.find((s) => s.includes('@')) || '';
    const nombre = partes.filter((s) => s !== email).join(' ');
    return { nombre, email };
  });

  const bajarFactura = async (e) => {
    e.preventDefault();
    setErrF(''); setBajando(true);
    try {
      const r = await fetch(`${API_BASE}/api/empresas/pedido/${token}/factura?cif=${encodeURIComponent(cifF)}`);
      if (!r.ok) { setErrF(r.status === 403 ? T('f_mal') : T('p_fallo')); setBajando(false); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'factura.pdf'; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setErrF(T('p_fallo')); }
    setBajando(false);
  };

  const enviar = async (e) => {
    e.preventDefault();
    setMsg(null); setEnviando(true);
    try {
      const r = await enviarTarjetasEmpresa(token, parsear());
      setMsg({ ok: true, t: T('p_ok', { n: r.enviadas }) });
      setTexto('');
      await cargar();
    } catch (err) {
      setMsg({ ok: false, t: err?.message || T('p_fallo') });
    }
    setEnviando(false);
  };

  return (
    <main className="clase-page regalo-page">
      <div className="clase-page-inner">
        <div className="clase-page-head">
          <div className="clase-page-avatar"><Building2 size={32} strokeWidth={1.25} /></div>
          <div className="clase-page-head-info">
            <h1 className="clase-page-title">{T('p_titulo')}</h1>
            <p className="clase-page-resumen">{p.empresa_nombre} · {T('p_resumen', { u: p.unidades, i: Number(p.importe_por_tarjeta), t: Number(p.total_a_pagar).toFixed(2) })}</p>
          </div>
        </div>

        {!pagado && <div className="evento-banner evento-banner--warn"><AlertCircle size={18} /> <span>{T('p_pendiente')}</span></div>}
        {pagado && params.get('stripe') === 'success' && <div className="evento-banner evento-banner--ok"><CheckCircle2 size={18} /> <span>{T('p_exito')}</span></div>}
        <p className="empresas-nota">{T('p_guarda')}</p>

        {pagado && (
          <>
            <h2 className="empresas-h">{T('f_t')}</h2>
            <form className="modal-form regalo-form" onSubmit={bajarFactura}>
              <p className="empresas-nota">{T('f_ayuda')}</p>
              <input type="text" required maxLength={20} value={cifF} onChange={(e) => setCifF(e.target.value)} placeholder="CIF" />
              {errF && <div className="modal-error-general">{errF}</div>}
              <button type="submit" className="clase-page-cta" disabled={bajando || !cifF.trim()}>{T('f_btn')}</button>
            </form>
            <h2 className="empresas-h">{T('p_enviar_t')}</h2>
            {porEnviar > 0 ? (
              <form className="modal-form regalo-form" onSubmit={enviar}>
                <p className="empresas-nota">{T('p_enviar_ayuda', { n: porEnviar })}</p>
                <textarea rows={7} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder={T('p_placeholder')} required />
                {msg && <div className={msg.ok ? 'evento-banner evento-banner--ok' : 'modal-error-general'}>{msg.t}</div>}
                <button type="submit" className="clase-page-cta" disabled={enviando || !texto.trim()}>
                  {enviando ? <><Loader2 size={16} className="spin" /> {T('p_enviando')}</> : T('p_enviar')}
                </button>
              </form>
            ) : null}

            <h2 className="empresas-h">{T('p_estado_t')}</h2>
            <div className="empresas-tabla">
              <div className="fila cab"><span>{T('col_dest')}</span><span>{T('col_estado')}</span></div>
              {tarjetas.map((c) => (
                <div className="fila" key={c.id}>
                  <span>{c.destinatario_nombre ? `${c.destinatario_nombre} · ${c.destinatario_email}` : '—'}</span>
                  <span className={'est est-' + c.estado}>{T('e_' + c.estado)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
